import { forwardRef } from "react";
import type { HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * Level 1: Page-level heading. One per page.
 * Use for the main title of the page (e.g. "Dashboard", "Settings").
 */
const PageTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h1
      ref={ref}
      className={cn("text-3xl font-bold leading-tight text-text-1", className)}
      {...props}
    />
  )
);
PageTitle.displayName = "PageTitle";

/**
 * Level 2: Section-level heading.
 * Use for major sections on the page (e.g. "Suggested mentors", "Mentorship groups").
 */
const SectionTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2
      ref={ref}
      className={cn("text-xl font-semibold leading-tight text-text-1", className)}
      {...props}
    />
  )
);
SectionTitle.displayName = "SectionTitle";

/**
 * Level 3: Heading inside a section (block/card).
 * Use for subsections, card titles, or blocks within a section (e.g. "Timeline", "Attendees").
 */
const BlockTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("text-lg font-semibold leading-tight text-text-1", className)}
      {...props}
    />
  )
);
BlockTitle.displayName = "BlockTitle";

export { PageTitle, SectionTitle, BlockTitle };
